<template>
  <div class="processes-tab">
    <dx-data-grid
      :data-source="getProcessDataSource"
      :columns="getProcessColumns"
      @toolbar-preparing="onToolbarPreparing($event)"
      class="e-appointment-details"
      :row-alternation-enabled="true"
      :columnChooser="{
        enabled: false,
      }"
      :pager="{ visible: false }"
      :height="height"
      :selection="{
        mode: 'none',
      }"
      ref="process-tab-grid"
      @content-ready="onContentReady"
      @editor-preparing="onEditorPreparing"
      :focusedRowEnabled="popupMode === 'edit'"
      keyExpr="id"
      @editing-start="onEditingStart"
      @row-removed="onRowRemoved"
      @row-prepared="onRowPrepared"
    >
      <!-- <DxScrolling
        :mode="'virtual'"
        :row-rendering-mode="'virtual'"
        :preload-enabled="true"
      /> -->

      <DxKeyboardNavigation
        :edit-on-key-press="true"
        :enter-key-action="'startEdit'"
        :enter-key-direction="'none'"
        :enabled="true"
      />

      <DxEditing
        :allow-deleting="false"
        :allow-updating="popupMode === 'edit'"
        :mode="'cell'"
        :changes="changes"
        :confirm-delete="false"
      />
    </dx-data-grid>
  </div>
</template>

<script>
import ArrayStore from "devextreme/data/array_store";
import ColumnWidth from "../../../_helpers/data-grid/ColumnWidth";
import dataGridMixin from "../../../_mixins/data-grid.mixin";
import { WorkOrderService } from "../../../_services/api/production/work-order.service";
import SettingsService from "../../../_services/api/settings/graycorp/settings.service";
// import { FeatureStorage } from "../../../_services/storage";
import DataGridHelpers from "@/_helpers/data-grid/helpers";
import { backwardTabbing, forwardTabbing } from "../../../_helpers/ui/keyboard";
const { countLineBreaks } = DataGridHelpers;

const baseLine = {
  Comment: "",
  IsDeletable: "Y",
  ProcessCode: "",
  ProcessDescription: "",
  RatePerHour: 0,
  RecordId: "",
  SetupHours: 0,
  SetupMinutes: 0,
  TotalProcessMinutes: 0,
  UnitProcessHours: 0,
  UnitProcessMinutes: 0,
  WorkOrderCode: "",
};

export default {
  mixins: [dataGridMixin],
  components: {},
  props: {
    dataModel: {
      type: Object,
      default: () => ({}),
    },
    height: {
      type: [Number, String],
      default: 280,
    },
    displayServerError: {
      type: Function,
      default: function () {},
    },
    showLoading: {
      type: Function,
      default: () => {},
    },
    popupMode: {
      type: String,
      default: "create",
    },
    refreshOnUpdate: {
      type: Boolean,
      default: false,
    },
    selectedTabIndex: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      WorkOrderInstance: "Core",
      changes: [],
      processDataSource: new ArrayStore({
        key: "id",
        data: [],
      }),
    };
  },
  async created() {
    this.showLoading(true);
    await this.loadWorkOrderInstance();
    await this.getWorkOrderProcessList();

    if (this.popupMode === "edit") {
      this.addNewRowToGrid();
    }
  },
  methods: {
    async getWorkOrderProcessList() {
      this.processDataSource.clear();
      await this.$nextTick();
      try {
        const response = await WorkOrderService.getWorkOrderProcessList(
          this.dataModel.WorkOrderCode,
        );

        if (response && response.data && response.data.value) {
          response.data.value.forEach((element) => {
            this.processDataSource.insert(element);
          });
        }
      } catch (error) {
        this.displayServerError(error, "Failed to load processes");
      } finally {
        this.showLoading(false);
      }
    },
    addNewRowToGrid(refresh = false) {
      this.processDataSource.insert({
        ...baseLine,
        WorkOrderCode: this.dataModel.WorkOrderCode,
      });

      if (refresh) {
        setTimeout(() => {
          this.dataGrid.saveEditData();
          this.dataGrid.refresh(true);

          this.$nextTick(() => {
            this.dataGrid.updateDimensions();
          });
        }, 100);
      }
    },
    async loadWorkOrderInstance() {
      let workOrderInstanceSetting = await SettingsService.getBySettingName(
        "WorkOrderInstance",
      );

      if (workOrderInstanceSetting.data.value.length > 0) {
        this.WorkOrderInstance =
          workOrderInstanceSetting.data.value[0].SettingValue;
      }
    },
    /**
     * on so list grid toolbar preparing
     * @param e - event
     */
    onToolbarPreparing(e) {
      e.toolbarOptions.visible = false;
    },
    onContentReady(e) {
      this.addGridNoResults(e, {
        addNew: {
          class: "add-new",
          title: "No Process",
          message: "No Material Issue has been made to this work order",
        },
      });

      setTimeout(() => {
        this.$nextTick(() => {
          e.component.beginUpdate();
          e.component.updateDimensions();
          e.component.endUpdate();
        });
      }, 200);
    },
    commentFieldHeightUpdate(e) {
      let instance = e.component;
      let count = countLineBreaks(instance.instance().option().value);
      // Adjust the height of the comment field based on the number of line breaks
      // If there are less than 7 line breaks, set the height to count * 16
      // If there are 7 or more line breaks, set the height to 250px
      // This is to ensure that the comment field does not grow too large
      // and remains user-friendly.
      // The height is set to 16px per line break, with a maximum height of 250px
      if (count < 7) {
        if (count > 2) {
          instance.instance().beginUpdate();
          instance.instance().option("height", count * 16);
          instance.instance().endUpdate();
          this.dataGrid.updateDimensions();
        }
      } else {
        instance.instance().beginUpdate();
        instance.instance().option("height", 250);
        instance.instance().endUpdate();
        this.dataGrid.updateDimensions();
      }
    },
    onEditorPreparing(e) {
      if (e.dataField && e.dataField == "ProcessCode") {
        e.editorName = "dxSelectBox";
      }
      if (e.dataField && e.dataField == "Comment") {
        e.editorName = "dxTextArea";
      }

      if (e.parentType !== "searchPanel") {
        const { dataField, editorOptions, row } = e;
        // Only wrap handlers if not in the search panel
        if (!editorOptions) return;

        const wrapHandler = (handlerName) => {
          const original = editorOptions[handlerName];
          if (typeof original === "function") {
            editorOptions[handlerName] = (event) => {
              original(event, {
                column: { dataField },
                data: row.data,
                rowIndex: row.rowIndex,
              });
            };
          }
        };

        // Wrap each handler if it exists
        [
          "onValueChanged",
          "onItemClick",
          "onKeyDown",
          "onOpened",
          "onFocusIn",
        ].forEach(wrapHandler);
      }
    },
    onEditingStart(e) {
      const rowData = e.data;
      if (rowData && this.popupMode === "edit") {
        if (e.column.dataField) {
          if (
            rowData.IsDeletable === "N" &&
            e.column.dataField === "ProcessCode"
          ) {
            e.cancel = true;
          }
          this.$emit("onProcessTabEditingStart");
        }
      }
    },
    async onRowRemoved(e) {
      const dataField = "ProcessCode";
      const items = await this.processDataSource.load();
      if (items.length === 0) {
        this.processDataSource.insert(baseLine);
        e.component.refresh();
        await this.$nextTick();

        setTimeout(() => {
          this.focusOnEditableCell(0, dataField);
        }, 100);
      } else if (e.data && !e.data.ProcessCode) {
        this.processDataSource.insert(baseLine).then((line) => {
          e.component.refresh();
          this.$nextTick(() => {
            setTimeout(() => {
              const rowIndex = e.component.getRowIndexByKey(line.id);
              if (rowIndex !== -1) {
                this.focusOnEditableCell(rowIndex, dataField);
              }
              this.focusOnEditableCell(rowIndex, dataField);
            }, 100);
          });
        });
      } else {
        if (e.data && (!e.data.ProcessCode || e.data.ProcessCode === "")) {
          // this.processDataSource.insert(baseLine);
          e.component.refresh();
          await this.$nextTick();

          setTimeout(() => {
            this.focusOnEditableCell(items.length, dataField);
          }, 100);
        }
      }
    },
    onProcessCodeValueChanged(e, lineData) {
      if (!e.event) return;
      if (e.value) {
        let processCode = e.value;
        const component = e.component; // the dxSelectBox instance
        const processData = component
          .option("items")
          .find((item) => item.Code === processCode);

        if (processData) {
          if (!lineData.data.ProcessCode) {
            this.addNewRowToGrid(true);
          }
          this.processDataSource.update(lineData.data.id, {
            ProcessCode: processData.Code,
            ProcessDescription: processData.Description,
          });
        }
      }
    },
    focusOnEditableCell(rowIndex, col) {
      const column = col ? col : "ProcessCode";
      const dataGrid = this.dataGrid;
      const cellElement = dataGrid.getCellElement(rowIndex, column);
      dataGrid.focus(cellElement);
      setTimeout(() => {
        dataGrid.editCell(rowIndex, column);
      }, 10);
    },
    setFocusOnHeaderForm() {
      this.$emit("setFocusOnField", "Comments", false, true);
    },
    getProcessCodeSource(items) {
      let filter = [["Inactive", "=", "N"]];
      let processFilter = [];
      if (items && items.length > 0) {
        items.forEach((element) => {
          if (element.ProcessCode) {
            if (processFilter.length > 0) {
              processFilter.push("and");
            }
            processFilter.push(["Code", "<>", element.ProcessCode]);
          }
        });
      }

      if (processFilter.length > 0) {
        filter.push("and");
        filter.push(processFilter);
      }

      return WorkOrderService.dataSourceConfig("api/Process", {
        filter: filter,
        sort: "Code asc",
        select: "Code,Description,Inactive",
        paginate: true,
      });
    },
    async getProcessLines() {
      const items = await this.processDataSource.load({
        filter: [["ProcessCode", "<>", null], "and", ["ProcessCode", "<>", ""]],
      });

      return items.map((item) => {
        return {
          Comment: item.Comment || "",
          ProcessCode: item.ProcessCode,
          ProcessDescription: item.ProcessDescription,
          SetupMinutes: item.SetupMinutes,
          SetupHours: item.SetupHours,
          TotalProcessMinutes: item.TotalProcessMinutes,
          RatePerHour: item.RatePerHour,
          UnitProcessMinutes: item.UnitProcessMinutes,
          UnitProcessHours: item.UnitProcessHours,
          WorkOrderCode: item.WorkOrderCode,
          RecordId: item.RecordId || "00000000-0000-0000-0000-000000000000",
        };
      });
    },
    async updateDeletedProcessLines(deletedLines) {
      const emptyLine = this.processDataSource._array.find(
        (line) => !line.ProcessCode,
      );

      if (emptyLine) {
        this.processDataSource.remove(emptyLine.id);
      }

      deletedLines.forEach((line) => {
        line.IsDeleted = true;
        this.processDataSource.insert(line);
      });

      if (emptyLine) {
        delete emptyLine.id;
        this.processDataSource.insert(emptyLine);
      }

      await this.$nextTick();
      this.dataGrid.saveEditData();
      this.dataGrid.repaint();
      setTimeout(() => {
        this.$nextTick(() => {
          this.dataGrid.updateDimensions();
        });
      }, 1000);
    },

    onRowPrepared(e) {
      if (e.rowType === "data") {
        if (e.data.IsDeleted && this.popupMode === "edit") {
          e.rowElement.classList.add("alert-row");
          if (e.rowElement.classList.contains("dx-row-alt")) {
            e.rowElement.classList.remove("dx-row-alt");
          }
        }
      }
    },
  },
  computed: {
    dataGrid() {
      return this.$refs["process-tab-grid"].instance;
    },
    getProcessDataSource() {
      return this.processDataSource._array;
    },
    getProcessColumns() {
      return [
        {
          caption: "Process",
          dataField: "ProcessCode",
          width: 70,
          allowEditing: this.popupMode !== "create",
          cssClass: "edit-cell-textbox-field",
          editorType: "dxSelectBox",
          editorOptions: {
            // dataSource: WorkOrderService.dataSourceConfig("api/Process", {
            //   filter: ["Inactive", "=", "N"],
            //   sort: "Code asc",
            //   select: "Code,Description,Inactive",
            // }),
            onFocusIn: async (e, lineData) => {
              const items = await this.processDataSource.load({
                filter: ["id", "<>", lineData.data.id],
              });
              const selectBox = e.component;
              selectBox.option("dataSource", this.getProcessCodeSource(items));
              await this.$nextTick();
              setTimeout(() => {
                selectBox.focus();
                this.$nextTick(() => {
                  e.event.target.select();
                });
              }, 200);
            },
            onClosed: (e) => {
              const selectBox = e.component;
              selectBox.beginUpdate();
              selectBox.dispose();
              selectBox.endUpdate();
            },
            valueExpr: "Code",
            displayExpr: "Code",
            searchEnabled: true,
            searchExpr: ["Code", "Description"],
            dropDownOptions: {
              width: 200,
            },
            // onFocusIn: (e) => {
            //   e.event.target.select();
            // },
            itemTemplate: (data) => {
              return ` <div class="custom-auto-complete">
                    <span class="auto-complete-primary">
                      ${data.Code}
                    </span>
                    <span class="record-secondary gc-grid-code">
                      ${data.Description}
                    </span>
                  </div>`;
            },
            onValueChanged: (e, lineData) => {
              this.onProcessCodeValueChanged(e, lineData);
            },
            onItemClick: (e, lineData) => {
              if (e.event)
                this.focusOnEditableCell(lineData.rowIndex, "RatePerHour");
            },
            onKeyDown: (e, lineData) => {
              if (backwardTabbing(e) && lineData.rowIndex === 0) {
                e.event.preventDefault();
                this.setFocusOnHeaderForm();
              }
            },
          },
          setCellValue: async (newData, value, currentRowData) => {
            if (!currentRowData.ProcessCode) {
              this.addNewRowToGrid(true);
            }
            newData.ProcessCode = value;
            return newData;
          },
        },
        {
          caption: "Description",
          dataField: "ProcessDescription",
          minWidth: ColumnWidth.Description.minWidth,
          // width: ColumnWidth.Description.width,
          allowEditing: false,
        },
        {
          caption: "Rate/Hr",
          dataField: "RatePerHour",
          dataType: "number",
          width: ColumnWidth.Quantity2DecimalPoints.width,
          minWidth: ColumnWidth.Quantity2DecimalPoints.minWidth,
          format: "##,###,###,###,##0.0000",
          cssClass: "edit-cell-numberbox-field",
          editorOptions: {
            format: "##,###,###,###,##0.0000",
            min: 0,
            onFocusIn: (e) => {
              e.event.target.select();
            },
            onKeyDown: (e, lineData) => {
              if (backwardTabbing(e) && lineData.data.IsDeletable === "N") {
                e.event.preventDefault();

                if (lineData.rowIndex === 0) {
                  this.setFocusOnHeaderForm();
                } else {
                  this.focusOnEditableCell(lineData.rowIndex - 1, "Comment");
                }
              }
            },
          },
          setCellValue: async (newData, value) => {
            newData.RatePerHour = value;

            if (value > 0) {
              newData.UnitProcessHours = (1 / value).roundNumber(4);
              newData.UnitProcessMinutes = (60 / value).roundNumber(4);
            } else {
              newData.UnitProcessHours = 0;
              newData.UnitProcessMinutes = 0;
            }

            return newData;
          },
          allowEditing: this.popupMode !== "create",
        },
        {
          caption: "Setup (Mins)",
          dataField: "SetupMinutes",
          dataType: "number",
          width: ColumnWidth.Quantity2DecimalPoints.width,
          minWidth: ColumnWidth.Quantity2DecimalPoints.minWidth,
          format: "##,###,###,###,##0.0000",
          cssClass: "edit-cell-numberbox-field",
          editorOptions: {
            format: "##,###,###,###,##0.0000",
            min: 0,
            onFocusIn: (e) => {
              e.event.target.select();
            },
          },
          setCellValue: async (newData, value) => {
            newData.SetupMinutes = value;
            newData.SetupHours = (value / 60).roundNumber(4);
            return newData;
          },
          allowEditing: this.popupMode !== "create",
        },
        {
          caption: "Setup (Hrs)",
          dataField: "SetupHours",
          dataType: "number",
          width: ColumnWidth.Quantity2DecimalPoints.width,
          minWidth: ColumnWidth.Quantity2DecimalPoints.minWidth,
          format: "##,###,###,###,##0.0000",
          cssClass: "edit-cell-numberbox-field",
          editorOptions: {
            format: "##,###,###,###,##0.0000",
            min: 0,
            onFocusIn: (e) => {
              e.event.target.select();
            },
          },
          setCellValue: async (newData, value) => {
            newData.SetupHours = value;
            newData.SetupMinutes = (value * 60).roundNumber(4);
            return newData;
          },
          allowEditing: this.popupMode !== "create",
        },
        {
          caption: "Process (Mins)",
          dataField: "UnitProcessMinutes",
          dataType: "number",
          width: ColumnWidth.Quantity2DecimalPoints.width,
          minWidth: ColumnWidth.Quantity2DecimalPoints.minWidth,
          format: "##,###,###,###,##0.0000",
          cssClass: "edit-cell-numberbox-field",
          editorOptions: {
            format: "##,###,###,###,##0.0000",
            min: 0,
            onFocusIn: (e) => {
              e.event.target.select();
            },
          },
          setCellValue: async (newData, value) => {
            newData.UnitProcessMinutes = value;

            if (value > 0) {
              newData.UnitProcessHours = (value / 60).roundNumber(4);
              newData.RatePerHour = (60 / value).roundNumber(4);
            } else {
              newData.UnitProcessHours = 0;
              newData.RatePerHour = 0;
            }
            return newData;
          },
          allowEditing: this.popupMode !== "create",
        },
        {
          caption: "Process (Hrs)",
          dataField: "UnitProcessHours",
          dataType: "number",
          width: ColumnWidth.Quantity2DecimalPoints.width,
          minWidth: ColumnWidth.Quantity2DecimalPoints.minWidth,
          format: "##,###,###,###,##0.0000",
          cssClass: "edit-cell-numberbox-field",
          editorOptions: {
            format: "##,###,###,###,##0.0000",
            min: 0,
            onFocusIn: (e) => {
              e.event.target.select();
            },
          },
          setCellValue: async (newData, value) => {
            newData.UnitProcessHours = value;

            if (value > 0) {
              newData.UnitProcessMinutes = (value * 60).roundNumber(4);
              newData.RatePerHour = (1 / value).roundNumber(4);
            } else {
              newData.UnitProcessMinutes = 0;
              newData.RatePerHour = 0;
            }

            return newData;
          },
          allowEditing: this.popupMode !== "create",
        },
        {
          caption: "Total Process (Mins)",
          dataField: "TotalProcessMinutes",
          name: "TotalProcessMinutes",
          dataType: "number",
          width: ColumnWidth.Quantity2DecimalPoints.width,
          minWidth: ColumnWidth.Quantity2DecimalPoints.minWidth,
          format: "##,###,###,###,##0.0000",
          allowEditing: false,
        },
        {
          caption: "Total Process (Hrs)",
          dataField: "TotalProcessMinutes",
          name: "TotalProcessHours",
          dataType: "number",
          width: ColumnWidth.Quantity2DecimalPoints.width,
          minWidth: ColumnWidth.Quantity2DecimalPoints.minWidth,
          format: "##,###,###,###,##0.0000",
          allowEditing: false,
          calculateDisplayValue: (e) => {
            if (e.TotalProcessMinutes > 0) {
              return (e.TotalProcessMinutes / 60).roundNumber(4);
            }
            return 0;
          },
        },
        // {
        //   dataField: "StartTime",
        //   minWidth: ColumnWidth.DateTime.minWidth,
        //   width: ColumnWidth.DateTime.width,
        //   dataType: "datetime",
        //   format: "dd MMM yyyy hh:mm:ss a",
        //   allowSorting: true,
        //   calculateDisplayValue: (e) => {
        //     if (e.StartTime) {
        //       return new Date(e.StartTime.replace("Z", ""));
        //     }
        //     return e.StartTime;
        //   },
        //   allowEditing: false,
        // },
        // {
        //   dataField: "EndTime",
        //   minWidth: ColumnWidth.DateTime.minWidth,
        //   width: ColumnWidth.DateTime.width,
        //   dataType: "datetime",
        //   format: "dd MMM yyyy hh:mm:ss a",
        //   allowSorting: true,
        //   calculateDisplayValue: (e) => {
        //     if (e.EndTime) {
        //       return new Date(e.EndTime.replace("Z", ""));
        //     }
        //     return e.EndTime;
        //   },
        //   allowEditing: false,
        // },

        {
          caption: "Comment",
          dataField: "Comment",
          cssClass: "edit-cell-textbox-field comment-text",
          minWidth: ColumnWidth.Comment.minWidth,
          customizeText: (e) => {
            if (e.value) return e.value.replaceAll("\n", "<br/>\r\n");
            else return "";
          },
          encodeHtml: false,
          cellTemplate: (data, info) => {
            return (data.innerHTML = `<div class='comment-cell-template'>${info.text}</div>`);
          },
          editorOptions: {
            onFocusIn: (e) => {
              e.event.target.select();
            },
            maxLength: 100,
            maxHeight: 103,
            // height: 30,
            valueChangeEvent: "input",
            onInput: (e) => {
              this.commentFieldHeightUpdate(e);
            },
            onInitialized: (e) => {
              this.commentFieldHeightUpdate(e);
            },
            onKeyDown: (e, lineData) => {
              if (forwardTabbing(e)) {
                if (
                  lineData.rowIndex ===
                  this.processDataSource._array.length - 1
                ) {
                  e.event.preventDefault();
                } else {
                  const nextRowID = this.dataGrid.getKeyByRowIndex(
                    lineData.rowIndex + 1,
                  );

                  if (nextRowID) {
                    this.dataGrid.byKey(nextRowID).done((nextRowData) => {
                      if (nextRowData && nextRowData.IsDeletable === "N") {
                        e.event.preventDefault();
                        this.focusOnEditableCell(
                          lineData.rowIndex + 1,
                          "RatePerHour",
                        );
                      }
                    });
                  }
                }
              }
            },
          },
          setCellValue: async (newData, value) => {
            newData.Comment = value;
            return newData;
          },
          allowEditing: this.popupMode !== "create",
        },
        {
          dataField: "",
          caption: "",
          type: "buttons",
          buttons: [
            {
              hint: "Delete",
              icon: "trash",

              visible: (e) => {
                const data = e.row.data;
                if (
                  data &&
                  data.IsDeletable === "Y" &&
                  this.popupMode === "edit"
                ) {
                  return true;
                } else {
                  return false;
                }
              },
              onClick: async (e) => {
                const confirmDelete = await this.$confirm(
                  "Are you sure you want to delete this process? This action cannot be undone unless re-added manually.",
                  "Delete Entry",
                );
                if (confirmDelete) {
                  this.dataGrid.deleteRow(e.row.rowIndex);
                }
              },
            },
          ],
          visible: this.popupMode === "edit",
        },
      ];
    },
  },
  watch: {
    popupMode(newValue) {
      if (newValue === "edit") {
        this.addNewRowToGrid();
      }
    },
    refreshOnUpdate(newValue) {
      if (newValue) {
        this.getWorkOrderProcessList();
      }
    },
    selectedTabIndex(newValue) {
      if (newValue) {
        setTimeout(() => {
          if (this.dataGrid) {
            this.$nextTick(() => {
              this.dataGrid.beginUpdate();
              this.dataGrid.updateDimensions();
              this.dataGrid.option("height", this.height);
              this.dataGrid.endUpdate();

              this.dataGrid.refresh(true);
            });
          }
        }, 200);
      }
    },
  },
};
</script>

<style lang="scss">
.processes-tab {
  .alert-row {
    background-color: #ffcccc; /* Light red background */
    // color: #a94442; /* Dark red text */
  }
}
</style>
